import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Phone, Play } from "lucide-react";
import { Link } from "react-router-dom";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_beaf72ee-9408-4045-89da-ab09df8a27f4/artifacts/r8zr67a7_ChatGPT%20Image%20Mar%2027%2C%202026%2C%2002_31_30%20AM.png";

const navLinks = [
  { label: "About", href: "#about" },
  { label: "Facilities", href: "#facilities" },
  { label: "Classes", href: "#classes" },
  { label: "Pricing", href: "#pricing" },
  { label: "Location", href: "#location" },
  { label: "Contact", href: "#contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleNav = (href) => {
    setMobileOpen(false);
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <>
      <motion.nav
        data-testid="navbar"
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
        className={`fixed z-50 transition-all duration-500 ${
          scrolled
            ? "top-4 left-4 right-4 lg:left-12 lg:right-12 bg-[#0A0A0A]/80 backdrop-blur-xl border border-white/10 shadow-2xl shadow-black/50 rounded-2xl"
            : "top-0 left-0 right-0 bg-transparent"
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 flex items-center justify-between h-20">
          <a href="#" onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })} className="flex items-center gap-3">
            <img src={LOGO_URL} alt="Core Culture" className="h-12 w-12 object-contain" />
            <span className="font-['Bebas_Neue'] text-2xl tracking-wider text-white hidden sm:block">
              CORE CULTURE
            </span>
          </a>

          <div className="hidden lg:flex items-center gap-8">
            {navLinks.map((link) => (
              <button
                key={link.href}
                data-testid={`nav-${link.label.toLowerCase()}`}
                onClick={() => handleNav(link.href)}
                className="text-sm font-medium tracking-wider uppercase text-[#A3A3A3] hover:text-[#F59E0B] transition-colors duration-300"
              >
                {link.label}
              </button>
            ))}
            <Link
              to="/gym-tour"
              className="flex items-center gap-2 px-6 py-2.5 rounded-full bg-white/5 border border-white/20 text-white font-bold text-sm tracking-widest uppercase hover:bg-white/10 hover:border-white/40 transition-all duration-300 transform hover:-translate-y-0.5"
            >
              <Play size={14} className="text-[#F59E0B]" />
              VIRTUAL TOUR
            </Link>
            <a
              href="tel:8826033661"
              data-testid="nav-phone"
              className="flex items-center gap-2 px-6 py-2.5 rounded-full bg-gradient-to-r from-[#F59E0B] to-[#D97706] text-black font-bold text-sm tracking-widest uppercase hover:shadow-[0_0_20px_rgba(245,158,11,0.4)] transition-all duration-300 transform hover:-translate-y-0.5"
            >
              <Phone size={14} />
              CALL NOW
            </a>
          </div>

          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setMobileOpen(!mobileOpen)}
            className="lg:hidden text-white p-2"
          >
            {mobileOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </motion.nav>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="fixed inset-0 z-40 bg-[#0A0A0A]/98 backdrop-blur-lg pt-24 px-8 lg:hidden"
          >
            <div className="flex flex-col gap-6">
              {navLinks.map((link, i) => (
                <motion.button
                  key={link.href}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: i * 0.08 }}
                  onClick={() => handleNav(link.href)}
                  className="text-left font-['Bebas_Neue'] text-4xl tracking-wider text-white hover:text-[#F59E0B] transition-colors"
                >
                  {link.label}
                </motion.button>
              ))}
              <Link
                to="/gym-tour"
                onClick={() => setMobileOpen(false)}
                className="mt-2 text-left font-['Bebas_Neue'] text-4xl tracking-wider text-[#F59E0B] hover:text-[#D97706] transition-colors"
              >
                VIRTUAL TOUR
              </Link>
              <a
                href="tel:8826033661"
                className="mt-4 flex items-center gap-3 px-6 py-4 bg-[#F59E0B] text-black font-semibold text-lg tracking-wider uppercase w-fit"
              >
                <Phone size={18} />
                8826033661
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
